import HyperDX from '@hyperdx/browser'
import { Crisp } from 'crisp-sdk-web'

import { useSystemStore } from '@/client/store/useSystemStore'

type ReportErrorMode = 'always' | 'server_or_unexpected'

export type ReportErrorContext = {
  feature?: string
  action: string
  screen?: string
  endpoint?: string
  httpStatus?: number
  errorCode?: string
  [key: string]: any
}

export type ReportErrorOptions = {
  mode?: ReportErrorMode
  excludeErrorCodes?: string[]
  includeNetworkError?: boolean
}

const isAxiosErrorLike = (error: unknown): error is { isAxiosError: boolean } => {
  return Boolean(error && typeof error === 'object' && 'isAxiosError' in error)
}

const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const response = (error as any).response
  const status = response?.status
  return typeof status === 'number' ? status : undefined
}

const getErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined
  const code = (error as any).response?.data?.error?.code
  return typeof code === 'string' ? code : undefined
}

const isLikelyNetworkError = (error: unknown): boolean => {
  if (!isAxiosErrorLike(error)) return false
  const hasResponse = Boolean((error as any).response)
  if (hasResponse) return false
  const message = typeof (error as any).message === 'string' ? (error as any).message : undefined
  const code = typeof (error as any).code === 'string' ? (error as any).code : undefined
  return (
    message === 'Network Error' ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'EHOSTUNREACH' ||
    code === 'ENETUNREACH'
  )
}

const shouldReport = (error: unknown, context: ReportErrorContext, options: ReportErrorOptions): boolean => {
  if (options.mode === 'always') return true

  const httpStatus = context.httpStatus ?? getHttpStatus(error)
  if (typeof httpStatus === 'number') {
    return httpStatus >= 500
  }

  if (!options.includeNetworkError && isLikelyNetworkError(error)) return false

  const excluded = new Set(options.excludeErrorCodes ?? [])
  const errorCode = context.errorCode ?? getErrorCode(error)
  if (errorCode && excluded.has(errorCode)) return false

  return true
}

const toSafeError = (error: unknown, context: ReportErrorContext): Error => {
  if (error instanceof Error) return error

  const feature = context.feature ?? 'unknown'
  const base = `ClientError: ${feature}:${context.action}`
  const httpStatus = context.httpStatus ?? getHttpStatus(error)
  const errorCode = context.errorCode ?? getErrorCode(error)
  const parts = [
    httpStatus ? `status=${httpStatus}` : null,
    errorCode ? `code=${errorCode}` : null,
    context.screen ? `screen=${context.screen}` : null,
    context.endpoint ? `endpoint=${context.endpoint}` : null
  ].filter(Boolean)
  const suffix = parts.length ? ` (${parts.join(', ')})` : ''

  return new Error(`${base}${suffix}`)
}

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, Object.getOwnPropertyNames(value as any))
  } catch {
    return String(value)
  }
}

export class TrackingService {
  /**
   * 初期化
   */
  static init(): void {
    Crisp.configure(process.env.CRISP_WEBSITE_ID)

    if (process.env.NODE_ENV === 'production') {
      HyperDX.init({
        apiKey: 'bc1138eb-8bc9-456b-9034-82a16966685e',
        service: 'atelu',
        tracePropagationTargets: [/atelu.comnico.jp\/api/i],
        consoleCapture: true,
        advancedNetworkCapture: true
      })
    } else {
      // HyperDXは別途料金が発生するため、開発環境の動作確認時のみコメントアウト
      // HyperDX.init({
      //   apiKey: 'bc1138eb-8bc9-456b-9034-82a16966685e',
      //   service: 'atelu-dev',
      //   tracePropagationTargets: [/dev-atelu.comnico.jp\/api/i],
      //   consoleCapture: true,
      //   advancedNetworkCapture: true
      // })
    }
  }

  /**
   * イベント送信
   */
  static sendEvent(name: string) {
    const systemStore = useSystemStore()
    if (!systemStore.user || !systemStore.currentGroup) return

    // GoogleAnalyticsにイベントを送信
    gtag('event', name, {
      event_category: systemStore.currentGroup.name,
      event_label: systemStore.user.name
    })

    // HyperDXは別途料金が発生するため、本番環境だけ送信。開発環境の動作確認時にコメントアウト
    if (process.env.NODE_ENV !== 'production') return

    // HyperDXにイベントを送信
    HyperDX.addAction(name, {})
  }

  /**
   * ログイン情報の送信
   */
  static sendLoginData() {
    const systemStore = useSystemStore()
    if (!systemStore.user || !systemStore.currentGroup) return

    if (Crisp.isCrispInjected()) {
      Crisp.user.setEmail(systemStore.user.email)
      Crisp.user.setNickname(systemStore.user.name)

      if (systemStore.user.pictureUrl) {
        Crisp.user.setAvatar(systemStore.user.pictureUrl)
      }

      const prefix = process.env.NODE_ENV === 'development' ? 'dev_' : process.env.NODE_ENV === 'staging' ? 'stg_' : ''

      Crisp.session.setSegments([`${prefix}atelu_user`], false)

      Crisp.session.setData({
        [`${prefix}atelu_user_id`]: systemStore.user.id,
        [`${prefix}atelu_recent_group_id`]: systemStore.currentGroup.id,
        [`${prefix}atelu_recent_group_name`]: systemStore.currentGroup.name
      })

      Crisp.session.pushEvent(`${prefix}atelu:login`)
    }

    // HyperDXは別途料金が発生するため、本番環境だけ送信。開発環境の動作確認時にコメントアウト
    if (process.env.NODE_ENV !== 'production') return

    // HyperDXにユーザーデータを送信。規定値が文字列のため、数値を文字列に変換
    HyperDX.setGlobalAttributes({
      userId: systemStore.user.id.toString(),
      userEmail: systemStore.user.email,
      userName: systemStore.user.name,
      groupId: systemStore.currentGroup.id.toString(),
      groupName: systemStore.currentGroup.name,
      groupContractStatus: systemStore.currentGroup.contractStatus.toString()
    })

    if (Crisp.isCrispInjected()) {
      Crisp.session.setData({ SessionURL: HyperDX.getSessionUrl() })
    }
  }

  static recordException(error: unknown, attributes: Record<string, any> = {}) {
    if (process.env.NODE_ENV !== 'production') return

    const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : safeStringify(error))

    HyperDX.recordException(err, attributes)
  }

  static reportError(error: unknown, context: ReportErrorContext, options: ReportErrorOptions = {}) {
    if (process.env.NODE_ENV !== 'production') return

    const resolvedOptions: ReportErrorOptions = { mode: 'server_or_unexpected', ...options }
    if (!shouldReport(error, context, resolvedOptions)) return

    const safeError = toSafeError(error, context)
    this.recordException(safeError, {
      ...context,
      httpStatus: context.httpStatus ?? getHttpStatus(error),
      errorCode: context.errorCode ?? getErrorCode(error)
    })
  }
}
